"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateDashboard = exports.updateDashboardName = exports.deactivateDashboardCollaborator = exports.activeDashboardCollaborator = exports.setDashboardClient = exports.resetDashboard = exports.setDashboard = exports.setDashboardLoading = exports.fetchSuccess = exports.setDashboardErrorCode = exports.fetchDashboardPack = void 0;
const api_1 = require("../../../../../../exports/api");
const config_1 = require("../../../../../../config");
const redux_batched_actions_1 = require("redux-batched-actions");
const store_1 = require("../../../../../../exports/store");
const actions_1 = require("../../../../../../exports/store/actions");
const selectors_1 = require("../../../../../../exports/store/selectors");
const widget_1 = require("../widget");
const action_constants_1 = require("../../../../../../modules/shared/store/action_constants");
const fetchDashboardPack = (dashboardId, successFn, overWrite = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const { shareId, templateId, embedId } = state.pageParams;
        const dashboardPack = (0, selectors_1.getDashboardPack)(state, dashboardId);
        const dashboardLoading = dashboardPack === null || dashboardPack === void 0 ? void 0 : dashboardPack.loading;
        const dashboard = dashboardPack === null || dashboardPack === void 0 ? void 0 : dashboardPack.dashboard;
        if (dashboardLoading) {
            return;
        }
        const fetchMethod = (() => {
            if (shareId) {
                return api_1.DashboardApi.fetchShareDashboardPack.bind(null, dashboardId, shareId);
            }
            if (templateId) {
                return api_1.DashboardApi.fetchTemplateDashboardPack.bind(null, dashboardId, templateId);
            }
            if (embedId) {
                return api_1.DashboardApi.fetchEmbedDashboardPack.bind(null, dashboardId, embedId);
            }
            return api_1.DashboardApi.fetchDashboardPack.bind(null, dashboardId);
        })();
        if (dashboard && !overWrite) {
            const installWidgetIds = (0, selectors_1.getInstalledWidgetInDashboard)(state) || [];
            if (installWidgetIds.length) {
                dispatch((0, actions_1.fetchWidgetsByWidgetIds)(installWidgetIds));
            }
            successFn === null || successFn === void 0 ? void 0 : successFn();
            return;
        }
        dispatch((0, exports.setDashboardLoading)(true, dashboardId));
        return fetchMethod()
            .then((response) => {
            return Promise.resolve({ dispatch, getState, response, dashboardId });
        })
            .catch((err) => {
            if (state.catalogTree.treeNodesMap[dashboardId]) {
                dispatch((0, actions_1.deleteNode)({ nodeId: dashboardId, parentId: state.catalogTree.treeNodesMap[dashboardId].parentId }));
            }
            dispatch((0, exports.setDashboardErrorCode)(dashboardId, config_1.StatusCode.COMMON_ERR));
            throw err;
        })
            .then((props) => {
            (0, exports.fetchSuccess)(props);
            props.response.data.success && (successFn === null || successFn === void 0 ? void 0 : successFn(props));
        });
    };
};
exports.fetchDashboardPack = fetchDashboardPack;
const setDashboardErrorCode = (dashboardId, code) => {
    return {
        type: store_1.ActionConstants.DASHBOARD_ERROR_CODE,
        payload: code,
        dashboardId,
    };
};
exports.setDashboardErrorCode = setDashboardErrorCode;
const fetchSuccess = ({ dispatch, response, dashboardId, }) => {
    const { data, success, code } = response.data;
    if (success) {
        const _batchActions = [(0, exports.setDashboard)(data.dashboard, data.dashboard.id)];
        if (Object.keys(data.widgetMap)) {
            for (const key in data.widgetMap) {
                _batchActions.push((0, widget_1.receiveInstallationWidget)(key, data.widgetMap[key]));
            }
        }
        dispatch((0, redux_batched_actions_1.batchActions)(_batchActions));
        return;
    }
    dispatch((0, exports.setDashboardErrorCode)(dashboardId, code));
};
exports.fetchSuccess = fetchSuccess;
const setDashboardLoading = (status, dashboardId) => {
    return {
        type: store_1.ActionConstants.SET_DASHBOARD_LOADING,
        payload: status,
        dashboardId: dashboardId,
    };
};
exports.setDashboardLoading = setDashboardLoading;
const setDashboard = (data, dashboardId) => {
    return {
        type: store_1.ActionConstants.SET_DASHBOARD_DATA,
        payload: data,
        dashboardId: dashboardId,
    };
};
exports.setDashboard = setDashboard;
const resetDashboard = (dashboardId) => {
    return {
        type: store_1.ActionConstants.RESET_DASHBOARD,
        dashboardId: dashboardId,
    };
};
exports.resetDashboard = resetDashboard;
const setDashboardClient = (client) => {
    return {
        type: store_1.ActionConstants.SET_DASHBOARD_CLIENT,
        payload: client,
    };
};
exports.setDashboardClient = setDashboardClient;
const activeDashboardCollaborator = (payload, resourceId) => {
    return {
        type: store_1.ActionConstants.DASHBOARD_ACTIVE_COLLABORATOR,
        dashboardId: resourceId,
        payload,
    };
};
exports.activeDashboardCollaborator = activeDashboardCollaborator;
const deactivateDashboardCollaborator = (payload, resourceId) => {
    return {
        type: store_1.ActionConstants.DASHBOARD_DEACTIVATE_COLLABORATOR,
        dashboardId: resourceId,
        payload,
    };
};
exports.deactivateDashboardCollaborator = deactivateDashboardCollaborator;
const updateDashboardName = (newName, dashboardId) => {
    return {
        type: action_constants_1.UPDATE_DASHBOARD_NAME,
        dashboardId,
        payload: newName,
    };
};
exports.updateDashboardName = updateDashboardName;
const updateDashboard = (dashboardId, data) => {
    return {
        type: action_constants_1.UPDATE_DASHBOARD_INFO,
        dashboardId,
        payload: data,
    };
};
exports.updateDashboard = updateDashboard;
//# sourceMappingURL=dashboard.js.map